import { Children, isValidElement, type ReactNode } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { CellHoverButtonsButton } from './nested/button'
import { CellHoverButtonsPopout } from './nested/popout'
import { CellHoverButtonsPrimary } from './nested/primary'
import { useHoverButtonsPosition } from './use-hover-buttons-position'

// Same choreography as the Action View footer's slot morph (HIG-5831), applied
// per button: a control that survives a state change slides to its new slot,
// only the ones actually appearing or leaving fade — archive turning into Undo
// under the pointer, not the whole pill blinking.
const BUTTON_VARIANTS: Variants = {
  initial: { opacity: 0, scale: 0.85, filter: 'blur(3px)' },
  animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
  exit: { opacity: 0, scale: 0.85, filter: 'blur(3px)', transition: { duration: 0.12, ease: 'easeOut' } },
}
const BUTTON_TRANSITION: Transition = { duration: 0.16, ease: 'easeOut' }

/**
 * Amplitude `trigger` for events initiated from this cluster, so the same
 * underlying action (invoke, archive, resolve, …) can be told apart from its
 * in-cell counterpart. Hosts on a surface with its own trigger vocabulary
 * (meetings) prefix it: `sidepanel-hover-cluster`.
 */
export const HOVER_CLUSTER_TRIGGER = 'hover-cluster'

interface CellHoverButtonsProps {
  className?: string
  /** Each child needs a stable `key` — it's what decides slide vs enter/exit across set changes. */
  children: ReactNode
}

/**
 * Floating action cluster for a hovered sidepanel cell (Figma 8425:91176).
 *
 * Hangs off a cell corner (bottom-right, or top-right behind the LD flag),
 * half outside it, so the actions read as handles on the row rather than as
 * part of its content. Expects a
 * positioned ancestor carrying `group/action` — the same wrapper every cell
 * host already uses for its hover styling — and appears on that group's hover.
 *
 * Hidden with opacity plus `pointer-events-none`, not `visibility`: a
 * transparent-but-present cluster must not swallow clicks meant for the row
 * under it, but `visibility: hidden` would also drop the buttons from the tab
 * order — they stay focusable so `focus-within` can reveal the cluster for
 * keyboard users.
 *
 * Hover is the only way in, for every host and every state — the undo windows
 * included (HIG-5928). There is deliberately no prop to pin the cluster open:
 * a control that outlives the pointer leaves the row it belongs to still
 * wearing a live button nobody is pointing at.
 */
function CellHoverButtons({ className, children }: CellHoverButtonsProps) {
  const position = useHoverButtonsPosition()
  return (
    <motion.div
      layout
      transition={BUTTON_TRANSITION}
      className={twMerge(
        // Concentric with the 28px buttons inside: their 14px corner plus the 3px padding.
        'corner-squircle absolute z-10 flex items-center gap-[2px] rounded-[17px] p-[3px]',
        position === 'top-right' ? '-right-[7px] -top-[9px]' : '-bottom-[9px] -right-[7px]',
        'bg-background shadow-lg',
        'pointer-events-none opacity-0',
        'group-hover/action:pointer-events-auto group-hover/action:opacity-100',
        // The hover preview card portals outside the group, so pointer-on-card
        // reveals via the trigger's data-popup-open instead of hover.
        'group-data-[popup-open]/action:pointer-events-auto group-data-[popup-open]/action:opacity-100',
        'focus-within:pointer-events-auto focus-within:opacity-100',
        'group-data-[preview-owner-inactive]/action:hidden',
        className,
      )}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {Children.toArray(children).map((child) => (
          <motion.div
            // toArray prefixes the child's own key ('.$undo'), so slots match
            // across renders as long as hosts key every button.
            key={isValidElement(child) ? child.key : undefined}
            layout
            variants={BUTTON_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={BUTTON_TRANSITION}
            className="flex items-center"
          >
            {child}
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  )
}

CellHoverButtons.Button = CellHoverButtonsButton
CellHoverButtons.Popout = CellHoverButtonsPopout
CellHoverButtons.Primary = CellHoverButtonsPrimary

export { CellHoverButtons }
export { POPOUT_LABEL, PopoutIcon } from './nested/popout'
